import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';

import BaseComponent from '../../../baseClasses/BaseComponent';
import projectsIcon from '../../../assets/images/projects-icon-light-gray.svg';
import LocationProjectHeader from './LocationProjectHeader';
import TabbedPageControl from '../../../components/TabbedPageControl';
import AttachmentsTable from './AttachmentsTable';
import AttachmentDisplay from './AttachmentsTable/AttachmentDisplay';
import LoadingDots from '../../../components/LoadingDots';
import dataModel from '../../../models/DataModel';
import DataSource from '../../../dao/DataSource/DataSource';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import ZipExportButton from '../ZipExportButton';
import EmailButton from '../EmailButton';
import progressReportsService from '../../../services/progressReportsService';
import { DOCUMENTS, INVENTORY, CLOSEOUT_HISTORY, PROGRESS_REPORTS } from '../../../constants/TabConstants';
import './PORProjectDocuments.scss';

export const location_root = {
  location: {
    market: {},
    location_users: { user: { additionalFields: ['profile_image'] } },
    users: {},
  },
};

/** List-only fetch for /location/:id/project/all — no documents/closeouts payload (user picks a project for the full table). */
const location_projects_list_only = {
  projects: { location: {} },
  project: { location: {} },
};

const location_projects_with_documents = {
  projects: {
    location: {},
    additionalFields: ['documents', 'closeouts', 'all_closeouts'],
    closeouts: { user: {} },
    all_closeouts: { user: {} },
    documents: { user: {}, excludeFields: ['upload', 'upload_thumb', 'upload_small', 'upload_annotated', 'attachment', 'conversions', 'photo_latitude', 'photo_longitude', 'photo_taken_at', 'location_source'] },
  },
  project: {
    location: {},
    additionalFields: ['documents', 'closeouts', 'all_closeouts'],
    closeouts: { user: {} },
    all_closeouts: { user: {} },
    documents: { user: {}, excludeFields: ['upload', 'upload_thumb', 'upload_small', 'upload_annotated', 'attachment', 'conversions', 'photo_latitude', 'photo_longitude', 'photo_taken_at', 'location_source'] },
  },
};

const location_projects_with_documents_and_inventory = {
  projects: {
    additionalFields: ['documents', 'closeouts', 'all_closeouts', 'inventory_items'],
    closeouts: { user: {} },
    all_closeouts: { user: {} },
    documents: { user: {}, excludeFields: ['upload', 'upload_thumb', 'upload_small', 'upload_annotated', 'attachment', 'conversions', 'photo_latitude', 'photo_longitude', 'photo_taken_at', 'location_source'] },
    inventory_items: {
      inventory: {},
      user: {},
      tasks: {},
    },
  },
  project: {
    additionalFields: ['documents', 'closeouts', 'all_closeouts', 'inventory_items'],
    closeouts: { user: {} },
    all_closeouts: { user: {} },
    documents: { user: {}, excludeFields: ['upload', 'upload_thumb', 'upload_small', 'upload_annotated', 'attachment', 'conversions', 'photo_latitude', 'photo_longitude', 'photo_taken_at', 'location_source'] },
    inventory_items: {
      inventory: {},
      user: {},
      tasks: {},
    },
  },
};

const attachmentDescriptor = { 
  attachment: { 
    user: {},
    additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'], 
  },
};

export class PORProjectDocuments extends BaseComponent {
  
  constructor(props) {
    super(props);
    this.state = {
      scrollHeightDiffFromHeader: 0,
      selectedTabName: DOCUMENTS,
      projects: [],
      progressReports: null, // null = not loaded yet, [] = loaded but empty
      selectedAttachment: null,
      initialized: false,
      loadingMore: true,
    };
    this.deleteCloseout = this.deleteCloseout.bind(this);
    this.deleteProgressReport = this.deleteProgressReport.bind(this);
    this.handleSelectProjectForDocuments = this.handleSelectProjectForDocuments.bind(this);
    // Single project selected: Documents → Inventory (if enabled) → Closeout history → Progress reports
    this.tabs = this.inventoryEnabled()
      ? [DOCUMENTS, INVENTORY, CLOSEOUT_HISTORY, PROGRESS_REPORTS]
      : [DOCUMENTS, CLOSEOUT_HISTORY, PROGRESS_REPORTS];
  }

  inventoryEnabled() {
    return this.props.currentCompany && this.props.currentCompany.features && this.props.currentCompany.features.inventory && this.props.currentCompany.features.inventory.enabled;
  }

  handleLocationRetrieval(location, action, acData, fetchedData) {
    if (location && this.state.location && this.state.location.id != location.id) {
      this.setState({ location, selectedAttachment: null });
    }
    else {
      this.setState({ location });
    }

    if (this.projectCollectionDataSource) {
      this.projectCollectionDataSource.current();
    }
    else if (this.projectDataSource) {
      this.projectDataSource.get();
    }
  }

  handleProjectRetrieval(project, action, acData, fetchedData) {
    const projectChanged = project && this.state.project && project.id !== this.state.project.id;
    const isActionCableUpdate = acData != null;
    const hasProgressReports = this.state.progressReports != null; // null/undefined = not loaded, [] = loaded but empty
    
    // Skip unnecessary updates from ActionCable events (e.g., status_update from step completions)
    // These don't change data we display on the progress reports page
    if (isActionCableUpdate && !projectChanged) {
      // Same project, ActionCable update - skip to prevent flickering
      return;
    }
    
    if (projectChanged) {
      this.setState({ loadingMore: false, project, initialized: !this.state.initialized && action !== 'prefetch', selectedAttachment: null });
      this.handleFetchProgressReportsForProject(project.id);
    }
    else {
      this.setState({ loadingMore: false, project, initialized: !this.state.initialized && action !== 'prefetch' });
      // Only fetch progress reports on initial load, not on ActionCable updates.
      // ActionCable updates for completion steps don't affect progress reports.
      // Note: Progress report generation triggers a separate notification banner, not this callback.
      if (project && project.id && !isActionCableUpdate && !hasProgressReports) {
        this.handleFetchProgressReportsForProject(project.id);
      }
    }
  }

  handleAttachmentRetrieval(attachment, action, acData, fetchedData) {
    if (!attachment) {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'The document you selected is no longer available.',
        captionText: 'ERROR',
        type: 'warning',
        oneButton: true,
        height: 163,
        width: 320,
        okButtonText: 'OK',
      });
    }
    else {
      this.setState({ selectedAttachment: attachment });
    }
  }

  handleCloseoutRetrieval(closeout, action, acData, fetchedData) {
    if (!closeout) {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'The document you selected is no longer available.',
        captionText: 'ERROR',
        type: 'warning',
        oneButton: true,
        height: 163,
        width: 320,
        okButtonText: 'OK',
      });
    }
    else {
      this.setState({ selectedAttachment: closeout });
    }
  }

  handleProgressReportRetrieval(progressReport) {
    if (!progressReport) {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'The progress report you selected is no longer available.',
        captionText: 'ERROR',
        type: 'warning',
        oneButton: true,
        height: 163,
        width: 320,
        okButtonText: 'OK',
      });
    }
    else {
      this.setState({ selectedAttachment: progressReport });
    }
  }

  handleProjectCollectionRetrieval(locationProjects, action, acData, fetchedData) {
    this.setState({
      locationProjects, initialized: !this.state.initialized && action != 'prefetch',
      loadingMore: !(action != 'prefetch'),
    });
  }

  handleInitializeProjectCollectionDataSource(location_id) {
    if (this.projectCollectionDataSource) {
      this.projectCollectionDataSource.release();
    }
    this.setState({ loadingMore: true });
    // "All projects" view: names only — full documents/closeouts load when user opens a project (see PORLocationDocuments).
    this.projectCollectionDataSource = new CollectionDataSource('LocationProjectsNavCollection', 'Project',
      location_projects_list_only,
      this.handleProjectCollectionRetrieval, 'LocationProjects', location_id, null,
      null, this.props.currentUser.id, this.props.currentCompany.id, location_id + '_forProjects');
  }

  handleInitializeLocationDataSource(location_id, project_id) {
    if (this.locationDataSource) {
      this.locationDataSource.release();
    }
    if (this.projectCollectionDataSource) {
      this.projectCollectionDataSource.release();
    }
    if (this.projectDataSource) {
      this.projectDataSource.release();
    }

    this.locationDataSource = new DataSource('Location', location_id, location_root, this.handleLocationRetrieval);

    if (project_id == 'all') {
      this.handleInitializeProjectCollectionDataSource(location_id);
    }
    else if (project_id && (typeof project_id == 'number')) {
      this.handleInitializeProjectDataSource(project_id, location_id);
    }

    this.locationDataSource.get();
  }
  
  handleInitializeProjectDataSource(project_id, location_id) {
    if (this.projectDataSource) {
      this.projectDataSource.release();
    }
    this.projectDataSource = new DataSource('Project', project_id, this.inventoryEnabled() ? location_projects_with_documents_and_inventory : location_projects_with_documents, this.handleProjectRetrieval, { location_id });
    this.projectDataSource.get();
  }
  
  handleInitializeAttachmentDataSource(attachment_id) {
    if (this.attachmentDataSource) {
      this.attachmentDataSource.release();
    }
    this.attachmentDataSource = new DataSource('Attachment', attachment_id, attachmentDescriptor, this.handleAttachmentRetrieval);
    this.attachmentDataSource.get();
  }
  
  handleInitializeCloseoutDataSource(closeout_id) {
    if (this.closeoutDataSource) {
      this.closeoutDataSource.release();
    }
    this.closeoutDataSource = new DataSource('Closeout', closeout_id, attachmentDescriptor, this.handleCloseoutRetrieval);
    this.closeoutDataSource.get();
  }
  
  componentDidMount() {
    const location_id = this.props.params.location_id;
    const project_id = this.props.params.project_id;
    const document_id = this.props.params.document_id;
    const closeout_id = this.props.params.closeout_id;
    const progress_report_id = this.props.params.progress_report_id;

    if (location_id) {
      this.handleInitializeLocationDataSource(location_id, project_id);
    }

    if (document_id) {
      this.setState({ selectedTabName: DOCUMENTS });
      this.handleInitializeAttachmentDataSource(document_id);
    }
    else if (closeout_id) {
      this.setState({ selectedTabName: CLOSEOUT_HISTORY });
      this.handleInitializeCloseoutDataSource(closeout_id);
    }
    else if (progress_report_id) {
      this.setState({ selectedTabName: PROGRESS_REPORTS });
      this.handleFetchProgressReport(progress_report_id);
    }
  }

  componentDidUpdate(prevProps) {
    const location_id = this.props.params.location_id;
    const project_id = this.props.params.project_id;
    const document_id = this.props.params.document_id;
    const closeout_id = this.props.params.closeout_id;

    if (location_id != prevProps.params.location_id || project_id != prevProps.params.project_id) {
      this.handleInitializeLocationDataSource(location_id, project_id);
      this.setState({ selectedAttachments: null });
      // All-location view: PROJECTS tab only (no inventory/closeout/progress here) — reset when coming from single-project tabs
      if (project_id == 'all' && prevProps.params.project_id != 'all') {
        this.setState((prev) => {
          const allowed = [DOCUMENTS];
          return allowed.includes(prev.selectedTabName) ? null : { selectedTabName: DOCUMENTS };
        });
      }
    }
    if (document_id || closeout_id) {
      if (document_id && document_id != prevProps.params.document_id) {
        this.handleInitializeAttachmentDataSource(document_id);
        this.setState({ selectedAttachments: null });
      }
      else if (closeout_id && closeout_id != prevProps.params.closeout_id) {
        this.handleFetchCloseout(closeout_id);
      }
    }
  }

  componentWillUnmount() {
    if (this.locationDataSource) {
      this.locationDataSource.release();
      this.locationDataSource = null;
    }
    if (this.projectDataSource) {
      this.projectDataSource.release();
      this.projectDataSource = null;
    }
    if (this.attachmentDataSource) {
      this.attachmentDataSource.release();
      this.attachmentDataSource = null;
    }
    if (this.closeoutDataSource) {
      this.closeoutDataSource.release();
      this.closeoutDataSource = null;
    }
    if (this.projectCollectionDataSource) {
      this.projectCollectionDataSource.release();
      this.projectCollectionDataSource = null;
    }
  }

  async handleFetchCloseout(closeout_id) {
    const { project } = this.state;
    let selectedAttachment;
    if (closeout_id) {
      let closeout = project.all_closeouts.find(c => c.id == closeout_id);
      if (!closeout) {
        this.handleInitializeCloseoutDataSource();
      }
      else {
        closeout = project.all_closeouts.find(c => c.id == closeout_id);
        
        selectedAttachment = closeout;
        if (!selectedAttachment) {
          dataModel.showGlobalModal('SYSTEM_POPUP', {
            message: 'The document you selected is no longer available.',
            captionText: 'ERROR',
            type: 'warning',
            oneButton: true,
            height: 163,
            width: 320,
            okButtonText: 'OK',
          });
        }
        else {
          this.setState({ selectedAttachment });
        }
      }
    }
  }

  async handleFetchProgressReport(progress_report_id) {
    const { progressReports } = this.state;
    let selectedAttachment = progressReports && progressReports.find(pr => pr.id == progress_report_id);
    
    if (!selectedAttachment) {
      const response = await progressReportsService.getProgressReport(progress_report_id);
      if (response && !response.error) {
        selectedAttachment = response.progressReport;
      }
    }
    
    if (selectedAttachment) {
      this.setState({ selectedAttachment });
    } else {
      this.handleProgressReportRetrieval(null);
    }
  }

  async handleFetchProgressReportsForProject(projectId) {
    if (!projectId) return;

    const response = await progressReportsService.getProgressReportsByProject(projectId);
    const progressReports = (response && !response.error && response.progressReports)
      ? response.progressReports
      : [];
    this.setState({ progressReports });
  }

  handleSelectForExport(selectedProjects, selectedAttachments) {
    if (selectedAttachments && !_.isEmpty(selectedAttachments)) {
      this.setState({ enableExport: true, selectedAttachments });
    }
    else {
      this.setState({ enableExport: false, selectedAttachments: {} });
    }
  }
  
  deleteCloseout(closeoutId) {
    const { location_id, project_id } = this.props.match.params;
    let { project, selectedAttachment } = this.state;
    let viewingAllProjects = false;
    if (!project) {
      viewingAllProjects = true;
      project = this.state.locationProjects.projects.find(p => {
        if (project_id && project_id != 'all') {
          return p.id == project_id;
        }
        else if (selectedAttachment && selectedAttachment.closeoutable_type == 'Project' && selectedAttachment.closeoutable_id == p.id) {
          return true;
        }
        else {
          return false;
        }
      });
      if (project) {
        project = _.cloneDeep(project);
      }
    }
    if (project) {
      const closeoutIndex = project.all_closeouts.findIndex(c => c.id == closeoutId);
      const deletedCloseout = project.all_closeouts.splice(closeoutIndex, 1)[0];
      if (!viewingAllProjects) {
        this.setState({ project });
      }
      if (this.props.updateProject) {
        this.props.updateProject(project);
      }
      this.props.history.push('/por/project/' + project_id + '/location/all?tab=closeouts');
      dataModel.closeoutsDAO.deleteCloseout(closeoutId)
      .catch(error => {
        project.all_closeouts.splice(closeoutIndex, 0, deletedCloseout);
        if (!viewingAllProjects) {
          this.setState({ project });
        }
        this.props.updateProject(project);
      });
    }
    
  }

  deleteProgressReport(progressReportId) {
    const { location_id, project_id } = this.props.match.params;
    progressReportsService.deleteProgressReportOptimistic({
      progressReportId,
      progressReports: this.state.progressReports,
      setState: this.setState.bind(this),
      history: this.props.history,
      location_id,
      project_id,
    });
  }

  handleSubHeaderExpand(expandAmount, className, animatorGroup, time) {
    if (animatorGroup) {
      const startHeight = window.innerHeight - this.state.scrollHeightDiffFromHeader;
      const endHeight = window.innerHeight - 18;
      const ani1 = this.animator.createPropertyAnimation(className, time, {
        height: { startVal: startHeight, endVal: endHeight },
      });
      animatorGroup.push(ani1);
    } else {
      this.setState({ scrollHeightDiffFromHeader: expandAmount });
    }
  }

  handleExpansion(height) {
    this.setState({ displayHeight: height });
    this.handleSubHeaderExpand(height, 'documents-detail-container');
    if (this.props.handleMapExpanded) {
      this.props.handleMapExpanded(height > 0 ? true : false);
    }
  }

  handleEmptyExpansion(height) {
    this.setState({ displayHeight: height });
    this.handleSubHeaderExpand(height, 'no-documents-container');
    if (this.props.handleMapExpanded) {
      this.props.handleMapExpanded(height > 0 ? true : false);
    }
  }

  handleSelectAttachment(attachment) {
    if ((!this.state.selectedAttachment && attachment) || !attachment || (attachment && this.state.selectedAttachment &&
      this.state.selectedAttachment.id != attachment.id))
      this.setState({ selectedAttachment: attachment });
  }

  handleTabClick(tabName) {
    this.switchToTab(tabName);
  }

  switchToTab(tabName) {
    if (this.state.selectedTabName != tabName) {
      this.viewKey = Math.floor(Math.random() * 100);
      this.setState({
        selectedTabName: tabName, toggleSelected: !this.state.toggleSelected,
        selectedAttachments: null, selectedProjects: null, selectedAttachment: null,
      });
    }
  }

  handleDownload() {
    this.setState({ toggleSelected: !this.state.toggleSelected });
    setTimeout(() => {
      this.setState({ selectedAttachments: null, selectedProjects: null });
    }, 100);
  }

  handleGoToProject(project) {
    this.props.history.push('/projects/' + project.id);
  }

  /** Same navigation as PORNav PORLocationParentCard → child project (opens project documents + locations table). */
  handleSelectProjectForDocuments(project) {
    if (project && project.id) {
      this.props.history.push('/por/project/' + project.id + '/location/all?tab=documents');
    }
  }

  render() {
    const { mapExpanded } = this.props;
    const { project_id, document_id, closeout_id } = this.props.params;
    const { locationProjects, loadingMore } = this.state;
    const { location, project, scrollHeightDiffFromHeader, selectedTabName } = this.state;

    if (!location) {
      return null;
    }
    const style = { top: scrollHeightDiffFromHeader + 61 };
    let mapDisplayed = '';
    if (scrollHeightDiffFromHeader > 0) {
      mapDisplayed = '--map-displayed';
    }

    let loadingDots = null;
    if (loadingMore) {
      loadingDots = <LoadingDots />;
    }

    const projects = locationProjects ? locationProjects.projects : null;
    const isAllProjectsAtLocation = project_id == 'all';
    const projectsListEmpty = !projects || (Array.isArray(projects) && projects.length === 0);

    let zipButton = null;
    let emailButton = null;
    let attachmentTable = null;
    let attachmentPreview = null;
    let noProjects = !projects && !project;
    let noDocuments = false;
    const { progressReports } = this.state;
    
    if (project) {
      noDocuments = (
        selectedTabName == DOCUMENTS &&
        (!project.documents || project.documents.length == 0)
      ) ||
        (
          selectedTabName == CLOSEOUT_HISTORY &&
          (!project.all_closeouts || project.all_closeouts.length == 0)
        ) ||
        (
          selectedTabName == PROGRESS_REPORTS &&
          (!progressReports || progressReports.length == 0)
        );

    }
    else if (projects) {
      noDocuments = project_id == 'all' && projects.filter(
        c => {
          return (
            (
              selectedTabName == DOCUMENTS &&
              c.documents && c.documents.length > 0
            ) ||
            (
              selectedTabName == CLOSEOUT_HISTORY &&
              c.all_closeouts && c.all_closeouts.length > 0
            ) ||
            (
              this.inventoryEnabled() && c.inventory_items && c.inventory_items.length > 0
            )
          );
        }
      ).length == 0;
    }
    if (!loadingMore) {
      const showAllProjectsEmpty = isAllProjectsAtLocation && projectsListEmpty;
      if (noProjects || showAllProjectsEmpty) {
        let emptyState;
        if (!project_id) {
          emptyState = (
            <div className="no-documents-container" style={style}>
              <img className="no-documents-icon" alt="no-docs" src={projectsIcon} />
              <div className="no-projects-text">To view Closeouts and Documents,<br />select a Project or the All Projects option.</div>
            </div>
          );
        }

        else{
          emptyState = (
            <div className="no-documents-container" style={style}>
              <img className="no-documents-icon" alt="no-docs" src={projectsIcon} />
              <div className="no-documents-text">No Projects found for this location.</div>
            </div>
          );
        }

        return (
          <div className="project-documents-container" key="no-documents">
            <LocationProjectHeader
              displayingAll={project_id == 'all'}
              project={project}
              location={location}
              handleExpansion={this.handleEmptyExpansion}
              closeDropdown={this.props.handleCloseDropdown}
              mapExpanded={mapExpanded}
            />
            {emptyState}
          </div>
        );
      } else if (!isAllProjectsAtLocation) {
        const { selectedAttachments } = this.state;
        
        attachmentTable = (
          <AttachmentsTable
            noDocumentsFound={noDocuments}
            projects={projects || project && [project]}
            progressReports={progressReports}
            selectedAttachmentId={document_id ? document_id : closeout_id}
            handleSelectAttachment={this.handleSelectAttachment}
            fileName={location.name}
            viewing={selectedTabName}
            currentCompany={this.props.currentCompany}
            handleSelectForExport={this.handleSelectForExport}
            toggleSelected={this.state.toggleSelected}
            gotoProject={this.handleGoToProject}
            initialized={this.state.initialized}
          />
        );
        if (selectedTabName != INVENTORY) {
          attachmentPreview =
            <AttachmentDisplay
              key={this.state.selectedAttachment ? this.state.selectedAttachment.id : 'all'}
              attachment={this.state.selectedAttachment}
              fromCloseoutDocument={this.props.fromCloseoutDocument}
              handleDeleteCloseout={this.deleteCloseout}
              handleDeleteProgressReport={this.deleteProgressReport}
              isProgressReport={selectedTabName == PROGRESS_REPORTS}
            />;
        }
          
        if (selectedTabName != INVENTORY) {
          if (selectedAttachments && !_.isEmpty(selectedAttachments)) {
            let fileName = location.name;
            let filenameWhich = null;
            let which = null;
           
            var selectedCloseouts=[];
            let nameSingleFile='';
            for(let selectedFiles in selectedAttachments) {     
              if (selectedFiles){
                selectedCloseouts.push(selectedFiles);    
              }       
                    
            }
            if (selectedCloseouts.length == 1) {
              for (const [key, value] of Object.entries(selectedAttachments)) {
                if (key && value){
                  let name = value.name.split(/\.(?=[\w])/);
                  nameSingleFile = name[0];
                }
               
              }
            }else{nameSingleFile='';}
          
            if (selectedTabName === DOCUMENTS) {
              filenameWhich = selectedCloseouts.length > 1 ? fileName + '-uploads' : nameSingleFile;
              which = 'attachments';
            } else if (selectedTabName === PROGRESS_REPORTS) {
              filenameWhich = selectedCloseouts.length > 1 ? fileName + '-progress-reports' : nameSingleFile;
              which = 'closeouts';
              // queryParamName='progressReportId' so backend receives progressReportId=... instead of closeouts=...
            } else {
              filenameWhich = selectedCloseouts.length > 1 ? fileName + '-closeouts' : nameSingleFile;
              which = 'closeouts';
            }
            let attachmentIds = [];
            let attachmentNames = [];
            for (let attachmentId in selectedAttachments) {
              if (attachmentId) {
                if (!attachmentIds.find(id => { return attachmentId == id; })) {
                  attachmentIds.push(attachmentId);
                  attachmentNames.push(selectedAttachments[attachmentId].name);
                }
              }
            }
            const zipQueryParamName = selectedTabName == PROGRESS_REPORTS ? 'progressReportId' : null;
            zipButton = (
              <div className="export-button-container">
                <ZipExportButton enabled={true} which={which} attachments={attachmentIds} filename={`${filenameWhich}`}
                  queryParamName={zipQueryParamName}
                  handleDownload={this.handleDownload} tab={this.state.selectedTabName} popupUbication={'POR'} />
              </div>
            );
          }
          else {
            zipButton = (
              <div className="export-button-container">
                <ZipExportButton enabled={false} />
              </div>
            );
          }
        }
        else {
          zipButton = (
            <div className="export-button-container">
              <ZipExportButton enabled={false} />
            </div>
          );
        }

        if(selectedTabName == CLOSEOUT_HISTORY){
          let attachmentIds = [];
          let attachmentNames = [];
          let attachmentCompany;
          for (let attachmentId in selectedAttachments) {
            if (attachmentId) {
              if (!attachmentIds.find(id => { return attachmentId == id; })) {
                attachmentIds.push(attachmentId);
                attachmentNames.push(selectedAttachments[attachmentId].name);
              }
            }
          }
          if (selectedAttachments && Object.keys(selectedAttachments).length > 0) {
            let firstAttachment = selectedAttachments[Object.keys(selectedAttachments)[0]];
            attachmentCompany = firstAttachment.company_id;
          }

          emailButton = (
            <div className="email-button-container">
              <EmailButton enabled={attachmentIds.length > 0} attachments={attachmentIds} companyId={attachmentCompany}
                marketId={this.state.location.market_id} handleUnselect={this.handleDownload}/>
            </div>
          );
        }
      }
    }

    if (isAllProjectsAtLocation) {
      const sortedProjects = projects && projects.length
        ? [...projects].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
        : [];
      // Project list only: single PROJECTS tab (no inventory — inventory appears with Documents/Closeout/Progress on a selected project)
      const tabsAllLocation = [DOCUMENTS];
      const tabLabelOverrides = { [DOCUMENTS]: 'projects' };

      let allLocationMainContent = null;
      if (loadingMore) {
        allLocationMainContent = loadingDots;
      }
      else if (selectedTabName === DOCUMENTS) {
        allLocationMainContent = (
          <AttachmentsTable
            projectsListOnly
            projects={sortedProjects}
            viewing={DOCUMENTS}
            gotoProject={this.handleSelectProjectForDocuments}
            gotoProjectList={this.handleGoToProject}
            listEmptyTitle="No Projects found for this location."
            currentCompany={this.props.currentCompany}
            initialized={true}
          />
        );
      }
      else if (!loadingMore) {
        allLocationMainContent = (
          <AttachmentsTable
            projectsListOnly
            projects={sortedProjects}
            viewing={DOCUMENTS}
            gotoProject={this.handleSelectProjectForDocuments}
            gotoProjectList={this.handleGoToProject}
            listEmptyTitle="No Projects found for this location."
            currentCompany={this.props.currentCompany}
            initialized={true}
          />
        );
      }

      return (
        <div
          className="project-documents-container"
          key={'location-projects-list-' + (location ? location.id : 0)}
        >
          <LocationProjectHeader
            displayingAll={true}
            project={null}
            location={location}
            handleExpansion={this.handleExpansion}
            closeDropdown={this.props.handleCloseDropdown}
            mapExpanded={mapExpanded}
          />
          <div className={'documents-detail-container' + (mapDisplayed != '' ? (' documents-detail-container' + mapDisplayed) : '')} style={style}>
            <div className="tabs-container">
              <TabbedPageControl
                tabItems={tabsAllLocation}
                tabLabelOverrides={tabLabelOverrides}
                selectedTabName={this.state.selectedTabName}
                handleClick={this.handleTabClick}
                borderTop={true}
              />
            </div>
            <div className={'documents-inner-container' + (mapDisplayed != '' ? (' documents-detail-container' + mapDisplayed) : '')}>
              <div className="attachments-table-container">
                {allLocationMainContent}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="project-documents-container"
        key={
          'location-documents-' +
          (location ? location.id : 0)
        }
      >
        <LocationProjectHeader
          displayingAll={!project}
          project={project}
          location={location}
          handleExpansion={this.handleExpansion}
          closeDropdown={this.props.handleCloseDropdown}
          mapExpanded={mapExpanded}
        />
        <div className={'documents-detail-container' + (mapDisplayed != '' ? (' documents-detail-container' + mapDisplayed) : '')} style={style}>
          <div className="tabs-container">
            <TabbedPageControl
              tabItems={this.tabs}
              selectedTabName={this.state.selectedTabName}
              handleClick={this.handleTabClick}
              borderTop={true}
            />
          </div>
          <div className={'documents-inner-container' + (mapDisplayed != '' ? (' documents-detail-container' + mapDisplayed) : '')}>
            <div className={'attachments-table-container' + (selectedTabName == 'inventory' ? ' no-preview' : '')}>
              {loadingDots}
              {zipButton}
              {emailButton}
              {attachmentTable}
            </div>

            {
              attachmentPreview &&
              <div className={'attachment-preview-container' + (mapDisplayed != '' ? (' attachment-preview-container' + mapDisplayed) : '')}>{attachmentPreview}</div>
            }
          </div>
        </div>
      </div>
    );
  }
}
const mapStateToProps = (state, ownProps) => {
  return {
    inProgress: state.main.inProgress,
    currentCompany: state.new_companies.currentCompany,
    currentUser: state.new_users.currentUser,
  };
};
export default withRouter(connect(mapStateToProps)(PORProjectDocuments));



// WEBPACK FOOTER //
// ./src/containers/PORContainer/PORProjectDocuments/index.js